import { createSelector, createSlice } from "@reduxjs/toolkit";
import { aggregatingFetch } from "../hooks/networkHook";
import { dataHookFactory, initMapFactory, initMultiMapFactory, useRealizationContext } from "./utilities";
import { ensureArray } from "../utils/arrayUtilities";
import { LazyValue, materializeValue } from "../utils/lazyValue";
import { logout } from "./initContext";
import { useMemo } from "react";

const blobSlice = createSlice({
    name: 'blob',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload).forEach(blob => state.data[blob.id] = blob.value);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => blobSlice.getInitialState());
    }
});

// #region network operations
const fetchBlob = aggregatingFetch(
    uuid => network => {
        return network.get('/api/v1/blob/' + encodeURIComponent(uuid), data => blobSlice.actions.set({ id: uuid, value: data }))
    },
    uuids => network => network.post('/api/v1/blobs', uuids, blobSlice.actions.set)
);
// #endregion

// #region selectors
const selectBlobData = state => state.blob.data;
const selectBlob = uuid => createSelector([selectBlobData], blobs => blobs[uuid]);
const selectBlobMap = uuids => createSelector([selectBlobData], blobs => uuids.reduce((obj, uuid) => { obj[uuid] = blobs[uuid]; return obj }, {}));
// #endregion

// #region listeners
// NB there are no listeners because blobs are immutable
const blobListen = {
};
// #endregion

const initMapByID = initMapFactory(fetchBlob, blobListen, { immutable: true })
export const [useBlob,realizeBlob] = dataHookFactory(selectBlob, initMapByID);
export const [useBlobMap,realizeBlobMap] = dataHookFactory(selectBlobMap, initMultiMapFactory(initMapByID, blobListen));

// #region utilities
/**
 * A special LazyValue that resolves using a realizeBlob
 * 
 * We can use realizeBlob even in the case of components / reactive contexts because
 * all blobs are immutable and so realize can mark entities fetched
 */
class ChronoDatum extends LazyValue {
    #uuid;
    constructor(realizationContext, uuid) {
        super(() => realizeBlob(realizationContext, uuid));
        this.#uuid = uuid;
    }
    toJSON() {
        return `chronodatum:${this.#uuid}`
    }
}

const chronoDatum = (realizationContext, uuid) => new ChronoDatum(realizationContext, uuid);

const CHRONO_URI_PREFIX = 'chronodatum:'

function lazyMaterializeChronodata(realizationContext, obj) {
    if (obj) {
        if (Array.isArray(obj)) {
            return obj.map(o => lazyMaterializeChronodata(realizationContext, o));
        } else if (typeof obj === 'object') {
            if (obj instanceof LazyValue) {
                return obj;
            } else {
                const toReturn = { ...obj };
                for (const field of Object.getOwnPropertyNames(obj)) {
                    toReturn[field] = lazyMaterializeChronodata(realizationContext, toReturn[field]);
                }
                return toReturn;
            }
        } else if (typeof obj === 'string' && obj.startsWith(CHRONO_URI_PREFIX)) {
            return chronoDatum(realizationContext, obj.slice(CHRONO_URI_PREFIX.length));
        }
    }
    return obj;
}

/**
 * This function will process an object and replace any chronodatum URIs with
 * the value of the blob as a value
 * @param {*} realizationContext standard context for running a realize
 * @param {*} obj the object to process
 * @returns a new object with chronodatum URIs resolved to values
 */
export async function materializeChronodata(realizationContext, obj) {
    if (obj) {
        if (Array.isArray(obj)) {
            const toReturn = [];
            for (const e of obj) {
                toReturn.push(await materializeChronodata(realizationContext, e));
            }
            return toReturn;
        } else if (typeof obj === 'object') {
            if (obj instanceof LazyValue) {
                return obj;
            } else {
                const toReturn = { ...obj };
                for (const field of Object.getOwnPropertyNames(obj)) {
                    toReturn[field] = await materializeChronodata(realizationContext, toReturn[field]);
                }
                return toReturn;
            }
        } else if (typeof obj === 'string' && obj.startsWith(CHRONO_URI_PREFIX)) {
            return await materializeValue(chronoDatum(realizationContext, obj.slice(CHRONO_URI_PREFIX.length)));
        }
    }
    return obj;
}

/**
 * This function will process an object and repalce any chronodatum URIs with
 * ta ChronoDatum object (a kind of LazyValue) which will be resolved on render
 * @param {*} data the object to process
 * @returns a new object with all chronodatums replaced with ChronoDatum lazy values
 */
export const useLazyMaterializeChronodata = (data) => {
    const realizationContext = useRealizationContext();
    return useMemo(() => lazyMaterializeChronodata(realizationContext, data), [realizationContext, data]);
}

// #endregion

const reducerDetails = { name: blobSlice.name, reducer: blobSlice.reducer };
export default reducerDetails;